"use client";

import { useState, useSyncExternalStore } from "react";

import { useTranslations } from "next-intl";

import { CONSENT_STORAGE_KEY } from "./SiteAnalytics";

type ConsentValue = "granted" | "denied";

function readConsent(): ConsentValue | null {
  try {
    const value = localStorage.getItem(CONSENT_STORAGE_KEY);
    return value === "granted" || value === "denied" ? value : null;
  } catch {
    return null;
  }
}

// Another tab (e.g. a snapshot page's banner) may store the choice first.
// Its choice is pushed to this tab's gtag too, which only read storage on load.
function subscribeToStorage(onChange: () => void) {
  const handle = (event: StorageEvent) => {
    if (event.key !== CONSENT_STORAGE_KEY) return;
    const value = readConsent();
    if (value) pushConsent(value);
    onChange();
  };
  window.addEventListener("storage", handle);
  return () => window.removeEventListener("storage", handle);
}

function pushConsent(value: ConsentValue) {
  const w = window as unknown as { dataLayer?: unknown[] };
  w.dataLayer = w.dataLayer || [];
  // gtag() pushes its `arguments` object, not an array; GTM only reads that shape.
  // eslint-disable-next-line prefer-rest-params
  const gtag = function (..._args: unknown[]) { w.dataLayer!.push(arguments); };
  gtag("consent", "update", {
    ad_storage: value,
    ad_user_data: value,
    ad_personalization: value,
    analytics_storage: value,
  });
}

function applyConsent(value: ConsentValue) {
  pushConsent(value);
  try {
    localStorage.setItem(CONSENT_STORAGE_KEY, value);
  } catch {
    /* storage blocked: the choice lasts for this page view only */
  }
}

/** folktale.ai's cookie banner; shown until the visitor makes a choice. */
export default function CookieConsent() {
  const translateText = useTranslations("website.cookies");
  // Server render and hydration see "server", so the banner only appears client-side.
  const stored = useSyncExternalStore(subscribeToStorage, readConsent, () => "server" as const);
  const [dismissed, setDismissed] = useState(false);

  if (stored !== null || dismissed) return null;

  const choose = (value: ConsentValue) => {
    applyConsent(value);
    setDismissed(true);
  };

  return (
    <div
      role="region"
      aria-label={translateText("aria")}
      className="fixed inset-x-0 bottom-0 z-50 border-t border-site-border bg-white/95 backdrop-blur"
    >
      <div className="mx-auto flex max-w-6xl flex-col gap-2 px-4 py-2 sm:flex-row sm:items-center sm:justify-between sm:gap-4 sm:py-2.5">
        <p className="text-xs leading-snug text-site-muted sm:text-sm">
          {translateText("message")}{" "}
          <a
            href="/cookie-policy"
            className="font-medium text-site-ink underline underline-offset-2 hover:text-site-forest"
          >
            {translateText("policy")}
          </a>
        </p>
        <div className="flex shrink-0 gap-2">
          <button
            type="button"
            onClick={() => choose("denied")}
            className="inline-flex flex-1 items-center justify-center rounded-md border border-site-border bg-site-bone px-3 py-1.5 text-xs font-medium transition-colors hover:bg-site-cream sm:flex-none sm:text-sm"
          >
            {translateText("essentialOnly")}
          </button>
          <button
            type="button"
            onClick={() => choose("granted")}
            className="inline-flex flex-1 items-center justify-center rounded-md bg-site-forest px-3 py-1.5 text-xs font-medium text-site-bone transition-colors hover:bg-site-forest-deep sm:flex-none sm:text-sm"
          >
            {translateText("acceptAll")}
          </button>
        </div>
      </div>
    </div>
  );
}
